import { GlButton, GlButtonGroup, GlDisclosureDropdown, GlIcon, GlLink, GlSprintf } from '../../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { s__ } from '~/locale';
import { helpPagePath } from '~/helpers/help_page_helper';
import HelpPopover from '../../../../../../../app/assets/javascripts/vue_shared/components/help_popover.vue?vue3';
import DuoWorkItemToMrAction from '../../../ai/shared/widgets/duo_work_item_to_mr_action.vue?vue3';
import ImplementationWidgetSegment from 'ee/vue_shared/components/work_items/implementation_widget_segment.vue';
import { WORKPLAN_GOAL_PREFIX, WORKPLAN_SOURCE, GENERATE_MR_BUTTON_OPTIONS, IMPLEMENTATION_UNAVAILABLE_MESSAGE, IMPLEMENTATION_HELP_PATH, GENERATION_STATUS_GENERATING, GENERATION_STATUS_NEEDS_INPUT, GENERATION_STATUS_CANCELED, GENERATION_STATUS_FAILED, GENERATION_STATUSES, WORKPLAN_ACTION } from './constants';
import { getAwaitingInputLabel, isValidReadinessScore, trackWorkplanImplement } from './utils.js?vue3';
import WorkplanQuestionNavigator from './workplan_question_navigator.vue?vue3';
import WorkplanStatusBar from './workplan_status_bar.vue?vue3';
import WorkplanAvailabilityPopover from './workplan_availability_popover.vue?vue3';
export default {
    name: 'WorkplanInlineRow',
    components: {
        ImplementationWidgetSegment,
        GlButton,
        GlButtonGroup,
        GlIcon,
        GlLink,
        GlSprintf,
        HelpPopover,
        GlDisclosureDropdown,
        DuoWorkItemToMrAction,
        WorkplanQuestionNavigator,
        WorkplanStatusBar,
        WorkplanAvailabilityPopover
    },
    props: {
        generationDisabledReason: {
            type: String,
            required: false,
            default: ''
        },
        hasContent: {
            type: Boolean,
            required: false,
            default: false
        },
        canUpdate: {
            type: Boolean,
            required: false,
            default: false
        },
        isPanelOpen: {
            type: Boolean,
            required: false,
            default: false
        },
        isLoading: {
            type: Boolean,
            required: true
        },
        workItemIid: {
            type: String,
            required: false,
            default: null
        },
        workItemType: {
            type: String,
            required: false,
            default: null
        },
        workItemWebUrl: {
            type: String,
            required: false,
            default: ''
        },
        readinessScore: {
            type: Number,
            required: false,
            default: null,
            validator: isValidReadinessScore
        },
        projectPath: {
            type: String,
            required: false,
            default: ''
        },
        generation: {
            type: Object,
            required: false,
            default: ()=>({}),
            validator: ({ status })=>status == null || GENERATION_STATUSES.includes(status)
        },
        isWorkItemClosed: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'open',
        'create-manually',
        'generate',
        'retry',
        'continue'
    ],
    computed: {
        helpPopoverOptions () {
            return {
                title: s__('AgentPlan|What is a workplan?')
            };
        },
        isAwaitingInput () {
            return this.generation.status === GENERATION_STATUS_NEEDS_INPUT;
        },
        hasFailedGeneration () {
            return this.generation.status === GENERATION_STATUS_FAILED;
        },
        isFlowActive () {
            return this.generation.isRunning;
        },
        showAwaitingInputIcon () {
            return this.isAwaitingInput;
        },
        showQuestionNavigator () {
            return this.isAwaitingInput && this.generation.openQuestionsCount > 0;
        },
        awaitingInputLabel () {
            const { openQuestionsCount, hasQuestions } = this.generation;
            if (!openQuestionsCount) {
                // A count of `0` also covers a run that paused without asking anything, such as on a
                // tool approval, so only claim every question is answered when some were asked.
                return hasQuestions ? s__('AgentPlan|All questions resolved') : s__('AgentPlan|Awaiting input');
            }
            return getAwaitingInputLabel(openQuestionsCount);
        },
        notStartedStatus () {
            return {
                label: this.canUpdate ? s__('AgentPlan|Not yet created') : s__('AgentPlan|No workplan'),
                variant: 'placeholder'
            };
        },
        statusByGenerationStatus () {
            return {
                [GENERATION_STATUS_GENERATING]: {
                    // A saved plan means the run is past its review pass, same as a resumed run,
                    // so only a run with neither reports that it is still reviewing.
                    label: this.generation.hasAwaitedInput || this.hasContent ? s__('AgentPlan|Generating workplan...') : s__('AgentPlan|Reviewing work item...'),
                    variant: 'info'
                },
                [GENERATION_STATUS_NEEDS_INPUT]: {
                    label: this.awaitingInputLabel,
                    variant: 'warning'
                },
                // Canceled runs aren't errors, but still offer the default actions rather than retry.
                [GENERATION_STATUS_CANCELED]: {
                    label: s__('AgentPlan|Generation canceled'),
                    variant: 'neutral'
                },
                [GENERATION_STATUS_FAILED]: {
                    label: s__('AgentPlan|Generation failed'),
                    variant: 'error'
                }
            };
        },
        status () {
            // An active run wins over content: on a regenerate the previous plan is still
            // saved, so its presence must not mask the run in progress.
            if (this.generation.isStarting) {
                return {
                    label: s__('AgentPlan|Preparing to generate...'),
                    variant: 'info'
                };
            }
            if (this.isFlowActive) {
                return this.statusByGenerationStatus[this.generation.status];
            }
            if (this.hasContent) {
                return {
                    label: s__('AgentPlan|Ready for review'),
                    variant: 'success'
                };
            }
            return this.statusByGenerationStatus[this.generation.status] ?? this.notStartedStatus;
        },
        showWorkplanToggle () {
            return this.hasContent && !this.isFlowActive;
        },
        showCreateWorkplanAction () {
            return !this.isLoading && !this.hasContent && this.canUpdate && !this.isFlowActive && !this.hasFailedGeneration;
        },
        showRetryAction () {
            return this.hasFailedGeneration && !this.hasContent && this.canUpdate;
        },
        showContinueAction () {
            return this.isAwaitingInput && this.canUpdate;
        },
        isRetryAction () {
            return this.primaryAction === WORKPLAN_ACTION.RETRY;
        },
        primaryAction () {
            if (this.showContinueAction) return WORKPLAN_ACTION.CONTINUE;
            if (this.showWorkplanToggle) return WORKPLAN_ACTION.VIEW;
            if (this.showRetryAction) return WORKPLAN_ACTION.RETRY;
            if (this.showCreateWorkplanAction) return WORKPLAN_ACTION.CREATE;
            return WORKPLAN_ACTION.NONE;
        },
        showAvailabilityPopover () {
            return Boolean(this.generationDisabledReason) && [
                WORKPLAN_ACTION.CREATE,
                WORKPLAN_ACTION.CONTINUE,
                WORKPLAN_ACTION.RETRY
            ].includes(this.primaryAction);
        },
        showGenerateMrAction () {
            return this.hasContent && this.canUpdate && !this.isFlowActive && !this.isWorkItemClosed;
        }
    },
    methods: {
        trackImplement () {
            trackWorkplanImplement('inline_row', this.readinessScore);
        }
    },
    createWorkplanDropdownItems: [
        {
            text: s__('AgentPlan|Create manually'),
            extraAttrs: {
                'data-testid': 'create-workplan-dropdown-item'
            }
        }
    ],
    WORKPLAN_GOAL_PREFIX,
    WORKPLAN_SOURCE,
    generateMrButtonOptions: GENERATE_MR_BUTTON_OPTIONS,
    IMPLEMENTATION_UNAVAILABLE_MESSAGE,
    IMPLEMENTATION_HELP_PATH,
    workplanHelpPath: helpPagePath('user/work_items/workplan'),
    WORKPLAN_ACTION
};
